import { useState, useEffect, useCallback } from 'react'
import { Settings } from 'lucide-react'
import { ChatBot, TokenAdapter, type AuthProvider } from './Chat'
import { ConfigurationModal } from './components/ConfigurationModal'
import { ChatErrorBoundary } from './components/ErrorBoundary'
import { ThemeToggle, ThemeSelector } from './components'
import { useTheme } from './components/ThemeProvider'
import { useCustomerTheme } from './lib/theme/useCustomerTheme'
import { getEnvConfig, validateEnvConfig } from './config/envConfig'
import {
  PREDEFINED_TOKENS,
  resolveInitialAuth,
} from './config/predefinedTokens'
import { log, logError } from './Chat/utils/logger'

/**
 * Interface for agent information from chat server
 */
interface Agent {
  id: string
  name: string
  description?: string
  max_engine_iterations?: number
  composition_mode?: string
  tags?: string[]
}

/**
 * Main App component demonstrating ChatBot integration
 * Features configuration panel with agent fetching
 */
function App() {
  // Load environment configuration
  const envConfig = getEnvConfig()
  const initialAuth = resolveInitialAuth(envConfig.defaultToken)

  // Validate environment variables on mount
  useEffect(() => {
    validateEnvConfig()
  }, [])

  // Complete config requires server, agent, and auth token
  const hasCompleteEnvConfig = !!(
    envConfig.serverUrl &&
    envConfig.agentId &&
    initialAuth?.token
  )
  
  const [showSettings, setShowSettings] = useState<boolean>(!hasCompleteEnvConfig)
  const [serverUrl, setServerUrl] = useState<string>(envConfig.serverUrl || 'http://localhost:8800')
  const [agentId, setAgentId] = useState<string>(envConfig.agentId || '')
  const [availableAgents, setAvailableAgents] = useState<Agent[]>([])
  const [isLoadingAgents, setIsLoadingAgents] = useState<boolean>(false)
  const [agentError, setAgentError] = useState<string>('')
  // Applied auth — drives ChatBot and API calls (resolved synchronously from env or localStorage)
  const [selectedToken, setSelectedToken] = useState<string | null>(initialAuth?.token ?? null)
  const [customerId, setCustomerId] = useState<string>(initialAuth?.customerId ?? '')
  const [customerName, setCustomerName] = useState<string>(initialAuth?.customerName ?? '')
  const [isCustomToken, setIsCustomToken] = useState<boolean>(initialAuth?.isCustomToken ?? false)

  // Pending auth — edited in Settings modal until Apply / Start Chat
  const [pendingToken, setPendingToken] = useState<string | null>(initialAuth?.token ?? null)
  const [pendingCustomerId, setPendingCustomerId] = useState<string>(initialAuth?.customerId ?? '')
  const [pendingCustomerName, setPendingCustomerName] = useState<string>(initialAuth?.customerName ?? '')
  const [pendingIsCustomToken, setPendingIsCustomToken] = useState<boolean>(initialAuth?.isCustomToken ?? false)

  // Custom token state
  const [customTokenInput, setCustomTokenInput] = useState<string>(
    initialAuth?.isCustomToken ? initialAuth.token : ''
  )
  const [customFirstName, setCustomFirstName] = useState<string>('')
  const [customLastName, setCustomLastName] = useState<string>('')
  const [authProvider, setAuthProvider] = useState<AuthProvider>('wordpressCustom')

  // Update theme when customerId changes
  useCustomerTheme(customerId)
  // When hideChatUi is true, never auto-enable chat even with complete env config
  const [isChatEnabled, setIsChatEnabled] = useState<boolean>(hasCompleteEnvConfig && !envConfig.hideChatUi)
  const [welcomeMessages, setWelcomeMessages] = useState<Record<'da' | 'en', string>>(envConfig.welcomeMessages)
  const [language, setLanguage] = useState<'da' | 'en'>(envConfig.language || 'da')
  const [initialMode, setInitialMode] = useState<'popup' | 'fullscreen' | 'minimized'>(envConfig.initialMode || 'popup')
  const [currentDisplayMode, setCurrentDisplayMode] = useState<'popup' | 'fullscreen' | 'minimized'>(envConfig.initialMode || 'popup')
  const [autoStartSession, setAutoStartSession] = useState<boolean>(envConfig.autoStartSession ?? true)
  
  // Hot corner state for fullscreen mode - shows controls when mouse is in top-right corner
  const [showHotCornerControls, setShowHotCornerControls] = useState<boolean>(false)

  const tokens = PREDEFINED_TOKENS

  /**
   * Fetches available agents from the chat server.
   * Skips unauthenticated fetch when a default token is configured — avoids
   * setting a stale agentError before auto-select runs on mount.
   */
  const fetchAgents = useCallback(async (): Promise<void> => {
    if (!serverUrl.trim()) return
    if (envConfig.defaultToken && !selectedToken) return

    setIsLoadingAgents(true)
    setAgentError('')

    try {
      const headers: Record<string, string> = {};
      if (selectedToken) {
        headers['Authorization'] = `Bearer ${selectedToken}`;
      }

      const response = await fetch(`${serverUrl}/agents`, {
        method: 'GET',
        headers
      })
      if (response.ok) {
        const agents: Agent[] = await response.json()
        setAvailableAgents(agents)

        // If we have agents and no current selection, use the first one
        if (agents.length > 0 && !agentId) {
          setAgentId(agents[0].id)
        }

        log('Fetched agents:', agents)
      } else {
        const errorText = `Failed to fetch agents: ${response.status} ${response.statusText}`
        setAgentError(errorText)
        logError(errorText)
      }
    } catch (error) {
      const errorMessage = `Error connecting to server: ${error instanceof Error ? error.message : 'Unknown error'}`
      setAgentError(errorMessage)
      logError('Error fetching agents:', error)
    } finally {
      setIsLoadingAgents(false)
    }
  }, [serverUrl, selectedToken, agentId, envConfig.defaultToken])

  /**
   * Commits pending auth from the settings modal to the applied state used by ChatBot.
   */
  const commitPendingAuth = (): void => {
    if (!pendingToken) return

    setSelectedToken(pendingToken)
    setCustomerId(pendingCustomerId)
    setCustomerName(pendingCustomerName)
    setIsCustomToken(pendingIsCustomToken)

    localStorage.setItem('parlant_auth_token', pendingToken)
    localStorage.setItem('parlant_customer_id', pendingCustomerId)
    localStorage.setItem('parlant_customer_name', pendingCustomerName)
  }

  /**
   * Handles the activation of the chatbot
   */
  const handleStartChat = (): void => {
    if (serverUrl.trim() && agentId.trim() && pendingToken) {
      commitPendingAuth()
      setIsChatEnabled(true)
      setShowSettings(false)
    }
  }

  /**
   * Toggles the settings panel visibility
   */
  const toggleSettings = (): void => {
    setShowSettings(!showSettings)
  }

  /**
   * Handles server URL change
   */
  const handleServerUrlChange = (url: string): void => {
    setServerUrl(url)
    setAgentError('')
  }

  /**
   * Handles predefined user selection in the Auth tab (pending until Apply).
   */
  const handlePendingTokenSelect = (tokenId: string): void => {
    const token = tokens.find(t => t.id === tokenId);
    if (token) {
      setPendingToken(token.token);
      setPendingCustomerId(token.customerId);
      setPendingCustomerName(token.name);
      setPendingIsCustomToken(false);
      setIsCustomToken(false);
      setAgentError('');

      log('Pending token selected:', {
        user: token.name,
        customerId: token.customerId,
        token: token.token.substring(0, 50) + '...'
      });
    }
  }

  // Reset pending auth to applied values whenever settings opens
  useEffect(() => {
    if (showSettings) {
      setPendingToken(selectedToken);
      setPendingCustomerId(customerId);
      setPendingCustomerName(customerName);
      setPendingIsCustomToken(isCustomToken);
    }
  }, [showSettings, selectedToken, customerId, customerName, isCustomToken]);

  /**
   * Extracts customer info from JWT payload using the appropriate auth provider adapter
   * @param token - The JWT token string
   * @param provider - The auth provider type
   * @returns Customer ID and optional name from the token payload
   */
  const extractCustomerInfoFromToken = (token: string, provider: AuthProvider): { customerId: string; customerName?: string } => {
    try {
      const adapter = new TokenAdapter(provider);
      const extracted = adapter.extractCustomerInfo(token);

      if (extracted?.customerId) {
        return {
          customerId: extracted.customerId,
          customerName: extracted.customerName,
        };
      }
    } catch (error) {
      logError('Failed to extract customer info from token:', error);
    }
    return { customerId: `custom_${Date.now()}` };
  }

  /**
   * Handles custom token submission — sets pending auth until Apply / Start Chat.
   */
  const handlePendingCustomTokenSubmit = (): void => {
    if (!customTokenInput.trim()) {
      return;
    }

    // Extract customer info using the selected auth provider
    const extracted = extractCustomerInfoFromToken(customTokenInput.trim(), authProvider);

    // Use extracted name from token if available, otherwise use manual input
    const manualName = (customFirstName.trim() && customLastName.trim())
      ? `${customFirstName.trim()} ${customLastName.trim()}`
      : undefined;
    const finalName = extracted.customerName || manualName || 'User';

    setPendingToken(customTokenInput.trim());
    setPendingCustomerId(extracted.customerId);
    setPendingCustomerName(finalName);
    setPendingIsCustomToken(true);
    setIsCustomToken(true);
    setAgentError('');

    log('Pending custom token set:', {
      user: finalName,
      customerId: extracted.customerId,
      authProvider,
      token: customTokenInput.trim().substring(0, 50) + '...'
    });
  }

  /**
   * Handles toggling between predefined and custom token mode
   * @param useCustom - Whether to use custom token mode
   */
  const handleCustomTokenToggle = (useCustom: boolean): void => {
    setIsCustomToken(useCustom);
    setPendingIsCustomToken(useCustom);
    if (useCustom) {
      setPendingToken(null);
      setPendingCustomerId('');
      setPendingCustomerName('');
    } else {
      setCustomTokenInput('');
      setCustomFirstName('');
      setCustomLastName('');
    }
  }

  /**
   * Handles when chat is closed
   */
  const handleChatClose = (): void => {
    log('Chat was closed/minimized')
  }

  /**
   * Handles when a session is created
   */
  const handleSessionCreated = (sessionId: string): void => {
    log('Session created:', sessionId)
  }

  // Refetch agents when server URL or applied auth token changes
  useEffect(() => {
    void fetchAgents()
  }, [fetchAgents])

  // Reset hot corner state when leaving fullscreen
  useEffect(() => {
    if (currentDisplayMode !== 'fullscreen') {
      setShowHotCornerControls(false)
    }
  }, [currentDisplayMode])

  // Get selected agent details
  const selectedAgent = availableAgents.find(agent => agent.id === agentId)

  // Get current theme to generate appropriate gradients
  const { themePreset, effectiveMode } = useTheme()

  /**
   * Generates gradient colors based on theme and mode
   * Each theme has its own primary hue that creates beautiful gradients
   */
  const getThemeGradients = (): { diagonal: string; radial: string } | null => {
    // Theme hue values (based on primary colors in defaultThemes.ts)
    const themeHues: Record<string, number> = {
      default: 220,     // Slate blue
      ocean: 185,       // Teal/cyan
      sunset: 15,       // Coral/orange
      purple: 270,      // Violet
      petroleum: 217,   // Petroleum blue
    }

    const hue = themeHues[themePreset] ?? 220

    if (effectiveMode === 'dark') {
      // Dark mode: dramatic, deep gradients with vibrant center
      return {
        diagonal: `linear-gradient(to bottom right, hsl(${hue}, 65%, 10%), hsl(${hue}, 60%, 25%), hsl(${hue}, 65%, 10%))`,
        radial: `radial-gradient(ellipse at 50% 50%, hsl(${hue}, 80%, 35%), hsl(${hue}, 50%, 15%) 70%)`
      }
    } else {
      // Light mode: subtle, soft gradients with elegant feel
      return {
        diagonal: `linear-gradient(to bottom right, hsl(${hue}, 30%, 96%), hsl(${hue}, 40%, 90%), hsl(${hue}, 30%, 96%))`,
        radial: `radial-gradient(ellipse at 50% 50%, hsl(${hue}, 50%, 95%), hsl(${hue}, 25%, 98%) 70%)`
      }
    }
  }

  const gradients = getThemeGradients()

  return (
    <div className="h-screen w-screen bg-background overflow-hidden relative">
      {/* Beautiful gradient overlays - adapts to all themes and modes */}
      {gradients && (
        <>
          {/* Diagonal gradient - creates depth with darker corners, lighter center */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: gradients.diagonal }}
          />
          {/* Radial gradient - adds vibrant center, creates atmospheric depth */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: gradients.radial }}
          />
        </>
      )}

      {/* Main Content Area */}
      <div className="relative h-full flex flex-col items-center justify-center p-4 sm:p-6 lg:p-8">
        {/* Hero Section */}
        <div className="text-center space-y-6 sm:space-y-8 max-w-4xl mx-auto px-2">
          <div className="space-y-2 sm:space-y-4">
            <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold text-foreground leading-tight">
              {selectedAgent?.name || envConfig.agentName}
            </h1>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-light text-muted-foreground">
              Chat Experience
            </h2>
          </div>

          <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed px-4">
            Experience intelligent conversations with our advanced AI chatbot.
            Configure your settings and start chatting instantly.
          </p>

          {!envConfig.hideChatUi && !isChatEnabled && (
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mt-8 sm:mt-12">
              <button
                onClick={toggleSettings}
                className="bg-card border border-border text-card-foreground px-6 sm:px-8 py-3 sm:py-4 rounded-xl hover:bg-muted transition-all duration-300 flex items-center space-x-2 shadow-lg hover:shadow-xl text-sm sm:text-base"
              >
                <Settings size={18} className="sm:w-5 sm:h-5" />
                <span>Configure Chat</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Configuration Modal */}
      {!envConfig.hideChatUi && (
        <ConfigurationModal
          isOpen={showSettings}
          onClose={toggleSettings}
          serverUrl={serverUrl}
          onServerUrlChange={handleServerUrlChange}
          agentId={agentId}
          onAgentIdChange={setAgentId}
          customerId={pendingCustomerId}
          onCustomerIdChange={setPendingCustomerId}
          selectedToken={pendingToken}
          onTokenSelect={handlePendingTokenSelect}
          appliedToken={selectedToken}
          appliedCustomerId={customerId}
          appliedIsCustomToken={isCustomToken}
          availableAgents={availableAgents}
          isLoadingAgents={isLoadingAgents}
          agentError={agentError}
          onFetchAgents={fetchAgents}
          onStartChat={handleStartChat}
          showStartButton={true}
          isChatActive={isChatEnabled}
          welcomeMessages={welcomeMessages}
          onWelcomeMessagesChange={setWelcomeMessages}
          language={language}
          onLanguageChange={setLanguage}
          initialMode={initialMode}
          onInitialModeChange={setInitialMode}
          autoStartSession={autoStartSession}
          onAutoStartSessionChange={setAutoStartSession}
          isCustomToken={isCustomToken}
          onCustomTokenToggle={handleCustomTokenToggle}
          customTokenInput={customTokenInput}
          onCustomTokenInputChange={setCustomTokenInput}
          customFirstName={customFirstName}
          onCustomFirstNameChange={setCustomFirstName}
          customLastName={customLastName}
          onCustomLastNameChange={setCustomLastName}
          onCustomTokenSubmit={handlePendingCustomTokenSubmit}
          authProvider={authProvider}
          onAuthProviderChange={setAuthProvider}
        />
      )}

      {/* ChatBot Component with Error Boundary */}
      {!envConfig.hideChatUi && isChatEnabled && serverUrl && agentId && selectedToken && (
        <ChatErrorBoundary>
          <ChatBot
            serverUrl={serverUrl}
            agentId={agentId}
            agentName={selectedAgent?.name || envConfig.agentName}
            authToken={selectedToken || undefined}
            authProvider={authProvider}
            customerId={customerId}
            customerName={customerName}
            requireAuth={true}
            language={language}
            initialMode={initialMode}
            onSessionCreated={handleSessionCreated}
            onClose={handleChatClose}
            onDisplayModeChange={setCurrentDisplayMode}
            autoStartSession={autoStartSession}
            enableLogging={envConfig.enableLogging}
            layoutDebug={envConfig.layoutDebug}
            pollingConfig={envConfig.pollingConfig}
            attribution={envConfig.showAttribution ? 'enter1nine' : 'none'}
            forceMinimize={showSettings}
            welcomeMessages={welcomeMessages}
            layout={{
              sessionHistory: {
                showSidebar: !envConfig.hideSessionSidebar,
                showMobileToggle: !envConfig.hideSessionSidebar,
              },
              mobileMenu: {
                showConversationsButton: !envConfig.hideSessionSidebar,
              },
            }}
          />
        </ChatErrorBoundary>
      )}

      {/* Settings Toggle, Theme Selector, and Theme Toggle (when chat is active) */}
      {/* In fullscreen mode: hover zone in corner reveals controls */}
      {/* In other modes: always visible */}
      {isChatEnabled && currentDisplayMode !== 'fullscreen' && (
        <div 
          className="fixed right-4 sm:right-8 flex items-center gap-2 z-[100]" 
          style={{ top: 'max(1rem, calc(env(safe-area-inset-top) + 0.5rem))' }}
        >
          <ThemeSelector size="sm" />
          <ThemeToggle size="sm" />
          <button
            onClick={toggleSettings}
            aria-label="Open settings"
            className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-card/80 backdrop-blur-md border border-border/50 shadow-lg hover:shadow-xl text-foreground hover:bg-card hover:scale-105 transition-all duration-200"
          >
            <Settings size={16} />
          </button>
        </div>
      )}
      
      {/* Fullscreen mode: tiny trigger zone in top-right corner (5-10px from edges) */}
      {isChatEnabled && currentDisplayMode === 'fullscreen' && (
        <>
          {/* Invisible trigger zone - very small, only in the corner */}
          <div 
            className="fixed top-0 right-0 z-[101] w-[150px] h-[10px]"
            onMouseEnter={() => setShowHotCornerControls(true)}
          />
          {/* Controls container - stays visible while hovered */}
          <div 
            className={`fixed top-0 right-0 z-[100] p-4 transition-opacity duration-300 ${
              showHotCornerControls ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
            onMouseLeave={() => setShowHotCornerControls(false)}
          >
            <div className="flex items-center gap-2 bg-background/60 backdrop-blur-xl rounded-2xl px-3 py-2 shadow-2xl border border-border/30">
              <ThemeSelector size="sm" />
              <ThemeToggle size="sm" />
              <button
                onClick={toggleSettings}
                aria-label="Open settings"
                className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-card/80 backdrop-blur-md border border-border/50 shadow-lg hover:shadow-xl text-foreground hover:bg-card hover:scale-105 transition-all duration-200"
              >
                <Settings size={16} />
              </button>
            </div>
          </div>
        </>
      )}

      {/* Theme Selector and Theme Toggle (when chat is not active) */}
      {!isChatEnabled && (
        <div className="fixed right-4 sm:right-8 flex items-center gap-2 z-[100]" style={{ top: 'max(1rem, calc(env(safe-area-inset-top) + 0.5rem))' }}>
          <ThemeSelector size="sm" />
          <ThemeToggle size="sm" />
        </div>
      )}
    </div>
  )
}

export default App